import React, { ReactNode } from 'react';
import { Drawer, Flex } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { CloseDrawerIcon } from '@/assets/icons/icons';
import CustomShipmentText from './CustomShipmentText';
import styles from './CommonDrawer.module.scss';

interface CommonDrawerProps {
  children: ReactNode;
  open: boolean;
  onClose: () => void;
  className?: string;
  onBack?: () => void;
  /** Text shown beside the back arrow (e.g. "User activity"). */
  backLabel?: string;
  width?: number;
  showHeader?: boolean;
  focusTrap?: boolean;
  /** Close on Escape; left to antd (true) when not set. */
  keyboard?: boolean;
  /** Close on a click outside; left to antd (true) when not set. */
  maskClosable?: boolean;

  headerActions?: ReactNode;
}

const CommonDrawer: React.FC<CommonDrawerProps> = ({
  children,
  open,
  onClose,
  className = '',
  onBack,
  backLabel,
  width = 444,
  showHeader = true,
  focusTrap = true, //drawer's focus back from that panel's inputs
  keyboard,
  maskClosable,
  headerActions,
}) => {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      className={`common-drawer ${className}`}
      bodyStyle={{ overflowY: 'auto' }}
      closable={false}
      // Only passed when a caller sets them, so other drawers keep antd's
      // (and any app-wide) defaults exactly.
      {...(keyboard === undefined ? {} : { keyboard })}
      {...(maskClosable === undefined ? {} : { maskClosable })}
      focusable={{ trap: focusTrap }}
      title={
        showHeader ? (
          <Flex align='center' justify='space-between'>
            <div className={styles.backSlot}>
              {onBack && (
                <Flex
                  align='center'
                  gap={8}
                  onClick={onBack}
                  role='button'
                  tabIndex={0}
                  aria-label={backLabel ? `Back to ${backLabel}` : 'Back'}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onBack();
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <ArrowLeftOutlined style={{ fontSize: 10 }} />
                  {backLabel && <CustomShipmentText text={backLabel} />}
                </Flex>
              )}
            </div>

            <Flex align='center' gap={12}>
              {headerActions}

              <span
                onClick={onClose}
                className={styles.closeButton}
                aria-label='Close drawer'
              >
                <CloseDrawerIcon size={16} />
              </span>
            </Flex>
          </Flex>
        ) : null
      }
    >
      {children}
    </Drawer>
  );
};

export default CommonDrawer;
